/**
 * Фоновая отправка «пропусков» анкет (свайп влево) на сервер.
 *
 * UI оптимистичный: карточка улетает сразу, запрос идёт в фоне. Чтобы пропуск
 * не терялся и не обгонялся «Вернуть»:
 *  • операции с одной анкетой выстраиваются в цепочку (undo не уйдёт раньше
 *    ещё не завершённого POST, иначе пропуск остался бы на сервере);
 *  • неудачный запрос повторяется с задержкой, а если не вышло — остаётся в
 *    хранилище и дошлётся при следующей загрузке ленты (`flushPending`);
 *  • перед загрузкой ленты `settle` ждёт хвост отправки, чтобы только что
 *    пропущенный не вернулся в ответе.
 * Чистая логика: сеть, хранилище и таймер приходят снаружи (юнит-тесты без DOM).
 */

export type PassOp = "pass" | "unpass";

export type PassApi = {
    pass: (userId: string) => Promise<{error: string | null}>;
    unpass: (userId: string) => Promise<{error: string | null}>;
};

export type PassStorage = {
    getItem: (key: string) => string | null;
    setItem: (key: string, value: string) => void;
};

type Pending = Record<string, {op: PassOp; fails: number}>;

/** После стольких неудачных проходов (с ретраями) операция сбрасывается — анкеты уже нет и т.п. */
export const MAX_PERSISTED_FAILS = 5;

/** Чей аккаунт сейчас залогинен в этой вкладке; null — никто (после выхода). */
let activeOwner: string | null = null;

/** Ключ очереди в localStorage: `<префикс>:<ownerId>`. */
export const PASS_STORAGE_PREFIX = "rl_feed_pending_passes_v1";

/** Ленту смонтировал владелец `ownerId` (или выход: null). Ретраи чужих экземпляров замолкают. */
export function setActivePassOwner(ownerId: string | number | null | undefined): void {
    activeOwner = ownerId == null ? null : String(ownerId);
}

/** Выход из аккаунта: остановить отправку и стереть очереди всех владельцев в браузере. */
export function resetPassQueuesOnLogout(storage?: (PassStorage & {removeItem?: (k: string) => void; key?: (i: number) => string | null; length?: number}) | null): void {
    activeOwner = null;
    try {
        const st = storage ?? (typeof window !== "undefined" ? window.localStorage : null);
        if (!st?.key || !st.removeItem || st.length == null) return;
        const keys: string[] = [];
        for (let i = 0; i < st.length; i++) {
            const k = st.key(i);
            if (k && k.startsWith(PASS_STORAGE_PREFIX)) keys.push(k);
        }
        keys.forEach((k) => st.removeItem!(k));
    } catch {
        /* хранилище недоступно — нечего чистить */
    }
}

export type PassSyncOptions = {
    /** Владелец очереди. Запросы уходят только пока он активный (setActivePassOwner): токен в api.ts один на вкладку. */
    ownerId?: string | number | null;
    api: PassApi;
    /** Без хранилища (нет owner) пропуски живут только в памяти. */
    storage?: PassStorage | null;
    storageKey?: string;
    retries?: number;
    backoffMs?: number;
    sleep?: (ms: number) => Promise<void>;
};

export function createPassSync(options: PassSyncOptions) {
    const {api, storage = null, storageKey = "rl_feed_pending_passes_v1", retries = 3, backoffMs = 400} = options;
    const sleep = options.sleep ?? ((ms: number) => new Promise<void>((resolve) => setTimeout(resolve, ms)));
    const owner = options.ownerId == null ? null : String(options.ownerId);
    const chains = new Map<string, Promise<void>>();

    const read = (): Pending => {
        if (!storage) return {...memory};
        try {
            const raw = storage.getItem(storageKey);
            const parsed = raw ? (JSON.parse(raw) as Pending) : {};
            return parsed && typeof parsed === "object" ? parsed : {};
        } catch {
            return {};
        }
    };
    const memory: Pending = {};
    const write = (value: Pending) => {
        if (!storage) {
            for (const key of Object.keys(memory)) delete memory[key];
            Object.assign(memory, value);
            return;
        }
        try {
            storage.setItem(storageKey, JSON.stringify(value));
        } catch {
            /* квота/приватный режим: остаётся только ретрай в памяти */
        }
    };

    const setPending = (userId: string, op: PassOp, fails = 0) => {
        const state = read();
        state[userId] = {op, fails};
        write(state);
    };
    const clearPending = (userId: string, op: PassOp) => {
        const state = read();
        // Более новая операция по той же анкете (pass → unpass) не затирается.
        if (state[userId]?.op === op) {
            delete state[userId];
            write(state);
        }
    };

    const attempt = async (userId: string, op: PassOp): Promise<boolean | null> => {
        for (let i = 0; i <= retries; i++) {
            // Сменился аккаунт: токен теперь чужой — не отправляем и не считаем неудачей.
            if (owner !== null && owner !== activeOwner) return null;
            try {
                const result = await (op === "pass" ? api.pass(userId) : api.unpass(userId));
                if (!result.error) return true;
            } catch {
                /* сетевой сбой — ретрай */
            }
            if (i < retries) await sleep(backoffMs * 2 ** i);
        }
        return false;
    };

    const run = (userId: string, op: PassOp): Promise<void> => {
        const prev = chains.get(userId) ?? Promise.resolve();
        let tail!: Promise<void>;
        const next = prev.then(async () => {
            try {
                // Пока ждали очередь, операцию могла перекрыть более новая — не шлём устаревшую.
                if (read()[userId]?.op !== op) return;
                const sent = await attempt(userId, op);
                if (sent === null) return; // чужой аккаунт: запись остаётся до его возвращения
                if (sent) {
                    clearPending(userId, op);
                    return;
                }
                const state = read();
                const fails = (state[userId]?.fails ?? 0) + 1;
                if (state[userId]?.op === op) {
                    if (fails >= MAX_PERSISTED_FAILS) delete state[userId];
                    else state[userId] = {op, fails};
                    write(state);
                }
            } finally {
                if (chains.get(userId) === tail) chains.delete(userId);
            }
        });
        tail = next.catch(() => undefined);
        chains.set(userId, tail);
        return next;
    };

    return {
        /** Свайп влево: запомнить и отправить в фоне. */
        pass(userId: string): Promise<void> {
            setPending(userId, "pass");
            return run(userId, "pass");
        },
        /** «Вернуть» после пропуска: снять пропуск (после ещё не ушедшего POST). */
        unpass(userId: string): Promise<void> {
            setPending(userId, "unpass");
            return run(userId, "unpass");
        },
        /** Дослать то, что не ушло раньше (перезагрузка страницы, обрыв сети). */
        flushPending(): Promise<void> {
            const jobs = Object.entries(read())
                .filter(([id]) => !chains.has(id))
                .map(([id, entry]) => run(id, entry.op));
            return Promise.all(jobs).then(() => undefined);
        },
        /** Дождаться отправки, но не дольше `timeoutMs` — лента не должна зависать из-за сети. */
        async settle(timeoutMs = 2500): Promise<void> {
            const all = Promise.all([...chains.values()]).then(() => undefined);
            await Promise.race([all, new Promise<void>((resolve) => setTimeout(resolve, timeoutMs))]);
        },
        /** Анкеты с неподтверждённым пропуском — их нельзя показывать из свежей загрузки. */
        pendingPassIds(): Set<string> {
            return new Set(Object.entries(read()).filter(([, e]) => e.op === "pass").map(([id]) => id));
        },
        pendingCount(): number {
            return Object.keys(read()).length;
        },
    };
}

export type PassSync = ReturnType<typeof createPassSync>;
